/**
 * Touch-drag camera look, standard mobile FPS pattern: drags that begin on
 * the right half of the screen rotate the camera (yaw/pitch); the left half
 * belongs to the joystick. Uses raw touch events so it coexists with the
 * TouchJoystick (which sits in its own DOM element and stops propagation
 * via preventDefault on its base).
 */
export class TouchLook {
  private readonly surface: HTMLElement;
  private readonly onLook: (dyaw: number, dpitch: number) => void;
  private touchId: number | null = null;
  private lastX: number = 0;
  private lastY: number = 0;
  private readonly sensitivity: number = 0.005;

  constructor(surface: HTMLElement, onLook: (dyaw: number, dpitch: number) => void) {
    this.surface = surface;
    this.onLook = onLook;
    this.surface.addEventListener("touchstart", this.handleStart, { passive: false });
    this.surface.addEventListener("touchmove", this.handleMove, { passive: false });
    this.surface.addEventListener("touchend", this.handleEnd, { passive: true });
    this.surface.addEventListener("touchcancel", this.handleEnd, { passive: true });
  }

  private handleStart = (e: TouchEvent): void => {
    if (this.touchId !== null) {
      return;
    }
    for (let i: number = 0; i < e.changedTouches.length; i++) {
      const t: Touch = e.changedTouches[i];
      if (t.clientX >= window.innerWidth / 2) {
        this.touchId = t.identifier;
        this.lastX = t.clientX;
        this.lastY = t.clientY;
        break;
      }
    }
  };

  private handleMove = (e: TouchEvent): void => {
    if (this.touchId === null) {
      return;
    }
    for (let i: number = 0; i < e.changedTouches.length; i++) {
      const t: Touch = e.changedTouches[i];
      if (t.identifier === this.touchId) {
        e.preventDefault();
        const dx: number = t.clientX - this.lastX;
        const dy: number = t.clientY - this.lastY;
        this.lastX = t.clientX;
        this.lastY = t.clientY;
        this.onLook(dx * this.sensitivity, dy * this.sensitivity);
        break;
      }
    }
  };

  private handleEnd = (e: TouchEvent): void => {
    for (let i: number = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.touchId) {
        this.touchId = null;
        break;
      }
    }
  };
}
